import { useMemo, useState } from "react";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";
import { TrendingUp, ArrowUpRight, Wallet } from "lucide-react";

const SPLIT = [
  { name: "Commander (Nessa)", short: "Commander", value: 80, color: "#D4AF37" },
  { name: "Fleet Treasury (EIGOO)", short: "Fleet Treasury", value: 10, color: "#9D80FF" },
  { name: "CyberKids Academy (Anchor)", short: "CyberKids · Anchor", value: 10, color: "#5DBDB5" },
];

const TRAJECTORY = [
  { m: "M01", v: 4.2 },
  { m: "M02", v: 5.6 },
  { m: "M03", v: 5.1 },
  { m: "M04", v: 7.4 },
  { m: "M05", v: 8.8 },
  { m: "M06", v: 10.2 },
  { m: "M07", v: 11.9 },
  { m: "M08", v: 13.8 },
  { m: "M09", v: 15.4 },
];

function ChartTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const p = payload[0];
  return (
    <div className="rounded-md border border-gold/30 bg-black/90 px-3 py-2 font-mono text-[11px]">
      <div className="text-white/60 tracking-[0.18em] uppercase text-[9px]">
        {p.payload.name || p.payload.m}
      </div>
      <div className="text-gold tracking-tight mt-0.5">
        {p.value}
        {p.payload.value !== undefined ? "%" : "K"}
      </div>
    </div>
  );
}

export default function RevenueDashboard() {
  const [active, setActive] = useState(0);
  const total = useMemo(() => SPLIT.reduce((s, x) => s + x.value, 0), []);

  return (
    <section id="revenue-dashboard" data-testid="revenue-dashboard">
      <SectionHeader
        eyebrow="Revenue Dashboard"
        title="The 80/10/10 Sovereign Rescue Split"
        subtitle="Eighty percent moves the Commander toward stability. Ten percent funds the Fleet Treasury that holds the ecosystem online. Ten percent anchors the next generation through CyberKids Academy. A doctrine, not a guess."
      />

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Donut card */}
        <div
          className="lg:col-span-2 rounded-2xl p-7 glass relative"
          data-testid="rescue-split-chart"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Wallet className="w-3.5 h-3.5 text-gold" />
              <span className="font-mono text-[10px] tracking-[0.28em] uppercase text-white/55">
                Allocation Map
              </span>
            </div>
            <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-gold/80">
              Vol 0
            </span>
          </div>

          <div className="relative mt-2 h-[260px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={SPLIT}
                  innerRadius={72}
                  outerRadius={108}
                  paddingAngle={3}
                  dataKey="value"
                  stroke="none"
                  onMouseEnter={(_, i) => setActive(i)}
                >
                  {SPLIT.map((entry, i) => (
                    <Cell
                      key={i}
                      fill={entry.color}
                      opacity={active === i ? 1 : 0.85}
                    />
                  ))}
                </Pie>
                <Tooltip content={<ChartTooltip />} />
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 grid place-items-center pointer-events-none">
              <div className="text-center">
                <div className="font-mono text-[9px] tracking-[0.3em] uppercase text-white/45">
                  {SPLIT[active].name}
                </div>
                <div className="mt-1 font-mono text-4xl gold-text tracking-tight">
                  {SPLIT[active].value}%
                </div>
                <div className="mt-1 font-mono text-[9px] tracking-[0.24em] uppercase text-white/40">
                  of {total}% command
                </div>
              </div>
            </div>
          </div>

          <div className="mt-4 space-y-2">
            {SPLIT.map((s, i) => (
              <button
                key={s.name}
                onMouseEnter={() => setActive(i)}
                onFocus={() => setActive(i)}
                data-testid={`split-row-${i}`}
                className={`w-full flex items-center justify-between px-3 py-2.5 rounded-md border transition-colors ${
                  active === i
                    ? "border-gold/40 bg-gold/5"
                    : "border-white/5 bg-black/40 hover:border-white/15"
                }`}
              >
                <div className="flex items-center gap-2.5">
                  <span
                    className="w-2 h-2 rounded-full"
                    style={{ background: s.color }}
                  />
                  <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-white/85">
                    {s.short}
                  </span>
                </div>
                <span
                  className="font-mono text-sm"
                  style={{ color: s.color }}
                >
                  {s.value}%
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Trajectory card */}
        <div
          className="lg:col-span-3 rounded-2xl p-7 glass"
          data-testid="rescue-trajectory-chart"
        >
          <div className="flex items-center justify-between mb-1">
            <div>
              <div className="flex items-center gap-2">
                <TrendingUp className="w-3.5 h-3.5 text-violet" />
                <span className="font-mono text-[10px] tracking-[0.28em] uppercase text-white/55">
                  Path to Stability
                </span>
              </div>
              <div className="mt-1 font-heading text-2xl text-white">
                Trajectory · 9 Month Outlook
              </div>
            </div>
            <div className="text-right">
              <div className="font-mono text-[9px] tracking-[0.28em] uppercase text-white/45">
                Compound Reserve
              </div>
              <div className="font-mono text-2xl gold-text">+267%</div>
              <div className="flex items-center justify-end gap-1 text-violet-soft font-mono text-[10px] tracking-[0.2em] uppercase">
                <ArrowUpRight className="w-3 h-3" /> velocity locked
              </div>
            </div>
          </div>

          <div className="h-[300px] mt-3">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={TRAJECTORY}
                margin={{ top: 10, right: 6, bottom: 0, left: -20 }}
              >
                <defs>
                  <linearGradient id="goldFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#D4AF37" stopOpacity={0.55} />
                    <stop offset="100%" stopColor="#D4AF37" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="rgba(255,255,255,0.04)" vertical={false} />
                <XAxis
                  dataKey="m"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 10, fontFamily: "IBM Plex Mono" }}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 10, fontFamily: "IBM Plex Mono" }}
                />
                <Tooltip content={<ChartTooltip />} />
                <Area
                  type="monotone"
                  dataKey="v"
                  stroke="#D4AF37"
                  strokeWidth={2}
                  fill="url(#goldFill)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </section>
  );
}

function SectionHeader({ eyebrow, title, subtitle }) {
  return (
    <div className="mb-7">
      <div className="flex items-center gap-2">
        <span className="w-1.5 h-1.5 rounded-full bg-gold animate-pulse-violet" />
        <span className="font-mono text-[10px] tracking-[0.32em] uppercase text-gold/90">
          {eyebrow}
        </span>
      </div>
      <h2 className="mt-3 font-heading text-3xl sm:text-4xl text-white tracking-tight">
        {title}
      </h2>
      {subtitle && (
        <p className="mt-3 max-w-2xl text-white/55 leading-relaxed">{subtitle}</p>
      )}
    </div>
  );
}
